<template>
  <div class="oss-config-management">
    <!-- 页面标题和操作栏 -->
    <el-card class="header-card" shadow="hover">
      <div class="header-content">
        <div class="title-section">
          <h2>
            <i class="el-icon-upload"></i>
            OSS配置管理
          </h2>
          <p>管理阿里云OSS配置，用于文档图片上传存储</p>
        </div>
        <div class="action-section">
          <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
            添加配置
          </el-button>
          <el-button icon="el-icon-refresh" @click="loadConfigList">
            刷新
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 配置列表 -->
    <el-card class="table-card" shadow="hover">
      <el-table
        :data="configList"
        v-loading="loading"
        stripe
        border
        style="width: 100%"
      >
        <el-table-column prop="configId" label="配置ID" width="100" />
        <el-table-column prop="configName" label="配置名称" width="180" />
        <el-table-column prop="endpoint" label="Endpoint" min-width="200" />
        <el-table-column prop="bucketName" label="Bucket名称" width="150" />
        <el-table-column prop="pathPrefix" label="路径前缀" width="120">
          <template slot-scope="scope">
            {{ scope.row.pathPrefix || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="customDomain" label="自定义域名" width="200">
          <template slot-scope="scope">
            {{ scope.row.customDomain || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="isEnabled" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.isEnabled === 1 ? 'success' : 'info'"
              size="small"
            >
              {{ scope.row.isEnabled === 1 ? '已启用' : '未启用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240">
          <template slot-scope="scope">
            <el-button 
              type="primary"
              size="mini"
              icon="el-icon-edit"
              @click="handleEdit(scope.row)"
            >
              编辑
            </el-button>
            <el-button 
              v-if="scope.row.isEnabled !== 1"
              type="success"
              size="mini"
              icon="el-icon-check"
              @click="handleEnable(scope.row.configId)"
            >
              启用
            </el-button>
            <el-button 
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="handleDelete(scope.row.configId)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 配置对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form ref="configForm" :model="formData" :rules="formRules" label-width="130px">
        <el-form-item label="配置名称" prop="configName">
          <el-input v-model="formData.configName" placeholder="请输入配置名称"></el-input>
        </el-form-item>
        <el-form-item label="Endpoint" prop="endpoint">
          <el-input v-model="formData.endpoint" placeholder="如：oss-cn-hangzhou.aliyuncs.com"></el-input>
        </el-form-item>
        <el-form-item label="AccessKey ID" prop="accessKeyId">
          <el-input v-model="formData.accessKeyId" placeholder="请输入AccessKey ID"></el-input>
        </el-form-item>
        <el-form-item label="AccessKey Secret" prop="accessKeySecret">
          <el-input 
            v-model="formData.accessKeySecret" 
            type="password" 
            placeholder="请输入AccessKey Secret"
            show-password
          ></el-input>
        </el-form-item>
        <el-form-item label="Bucket名称" prop="bucketName">
          <el-input v-model="formData.bucketName" placeholder="请输入Bucket名称"></el-input>
        </el-form-item>
        <el-form-item label="存储路径前缀" prop="pathPrefix">
          <el-input v-model="formData.pathPrefix" placeholder="如：documents（可选）"></el-input>
        </el-form-item>
        <el-form-item label="自定义域名" prop="customDomain">
          <el-input v-model="formData.customDomain" placeholder="如：https://cdn.example.com（可选）"></el-input>
        </el-form-item>
        <el-form-item label="是否启用" prop="isEnabled">
          <el-switch 
            v-model="formData.isEnabled" 
            :active-value="1" 
            :inactive-value="0"
          ></el-switch>
          <span style="margin-left: 10px; color: #999; font-size: 12px;">
            启用后会自动禁用其他配置
          </span>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { authUtils } from '@/utils/auth'

export default {
  name: 'OssConfigManagement',
  data() {
    return {
      configList: [],
      loading: false,
      dialogVisible: false,
      dialogTitle: '添加配置',
      isEditMode: false,
      submitting: false,
      formData: {
        configId: null,
        configName: '',
        endpoint: '',
        accessKeyId: '',
        accessKeySecret: '',
        bucketName: '',
        pathPrefix: '',
        customDomain: '',
        isEnabled: 0
      },
      formRules: {
        configName: [
          { required: true, message: '请输入配置名称', trigger: 'blur' }
        ],
        endpoint: [
          { required: true, message: '请输入Endpoint', trigger: 'blur' }
        ],
        accessKeyId: [
          { required: true, message: '请输入AccessKey ID', trigger: 'blur' }
        ],
        accessKeySecret: [
          { required: true, message: '请输入AccessKey Secret', trigger: 'blur' }
        ],
        bucketName: [
          { required: true, message: '请输入Bucket名称', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    if (!authUtils.isAdmin()) {
      this.$message.warning('无权限访问管理后台，已自动返回文档列表')
      this.$router.replace('/')
      return
    }
    this.loadConfigList()
  },
  methods: {
    // 加载配置列表
    async loadConfigList() {
      this.loading = true
      try {
        const response = await this.$get('/oss-config/list')
        if (response.data && response.data.code === 200) {
          this.configList = response.data.data
        } else {
          this.$message.error(response.data?.msg || '加载配置列表失败')
        }
      } catch (error) {
        this.$message.error(error.message || '加载配置列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    
    // 添加配置
    handleAdd() {
      this.dialogTitle = '添加配置'
      this.isEditMode = false
      this.resetForm()
      this.dialogVisible = true
    },
    
    // 编辑配置
    handleEdit(row) {
      this.dialogTitle = '编辑配置'
      this.isEditMode = true
      this.formData = {
        configId: row.configId,
        configName: row.configName,
        endpoint: row.endpoint,
        accessKeyId: row.accessKeyId,
        accessKeySecret: row.accessKeySecret,
        bucketName: row.bucketName,
        pathPrefix: row.pathPrefix || '',
        customDomain: row.customDomain || '',
        isEnabled: row.isEnabled
      }
      this.dialogVisible = true
    },
    
    // 启用配置
    async handleEnable(configId) {
      try {
        const response = await this.$post('/oss-config/enable', null, {
          params: { configId }
        })
        if (response.data && response.data.code === 200) {
          this.$message.success('启用成功')
          this.loadConfigList()
        } else {
          this.$message.error(response.data.msg || '启用失败')
        }
      } catch (error) {
        this.$message.error('启用失败')
        console.error(error)
      }
    },
    
    // 删除配置
    handleDelete(configId) {
      this.$confirm('确定要删除该配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await this.$post('/oss-config/delete', null, {
            params: { configId }
          })
          if (response.data && response.data.code === 200) {
            this.$message.success('删除成功')
            this.loadConfigList()
          } else {
            this.$message.error(response.data.msg || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败')
          console.error(error)
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.configForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        
        this.submitting = true
        try {
          const url = this.isEditMode ? '/oss-config/update' : '/oss-config/create'
          const response = await this.$post(url, this.formData)
          
          if (response.data && response.data.code === 200) {
            this.$message.success(this.isEditMode ? '更新成功' : '创建成功')
            this.dialogVisible = false
            this.loadConfigList()
          } else {
            this.$message.error(response.data.msg || '操作失败')
          }
        } catch (error) {
          this.$message.error('操作失败')
          console.error(error)
        } finally {
          this.submitting = false
        }
      })
    },
    
    // 重置表单
    resetForm() {
      this.formData = {
        configId: null,
        configName: '',
        endpoint: '',
        accessKeyId: '',
        accessKeySecret: '',
        bucketName: '',
        pathPrefix: '',
        customDomain: '',
        isEnabled: 0
      }
      if (this.$refs.configForm) {
        this.$refs.configForm.clearValidate()
      }
    },
    
    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.oss-config-management {
  padding: 0;
}

.header-card {
  margin-bottom: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section h2 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 20px;
  display: flex;
  align-items: center;
}

.title-section h2 i {
  margin-right: 8px;
  color: #409EFF;
}

.title-section p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.table-card {
  margin-bottom: 20px;
}

.dialog-footer {
  text-align: right;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .action-section {
    margin-top: 15px;
  }
}

@media (max-width: 480px) {
  .el-table {
    font-size: 12px;
  }
  
  .el-button--mini {
    padding: 5px 8px;
    font-size: 12px;
  }
}
</style>
